import { useBreakpoints } from '@inflow/ui/lib/useBreakpoints'
import { motion } from 'motion/react'
import type { PropsWithChildren, ReactNode } from 'react'
import { containerStagger, fadeUp } from '../base-animations'

export default function ({
  subTitle,
  title,
  children,
  desktopAlignment = 'left',
  mobileAlignment = 'center',
}: PropsWithChildren<{
  subTitle: ReactNode
  title: ReactNode
  mobileAlignment?: 'center' | 'left' // Marked optional to match default params
  desktopAlignment?: 'center' | 'left'
}>) {
  const { md } = useBreakpoints()

  // 1. Determine if we are currently on a "desktop" size
  // We use 'md' here because your original code used 'md:' prefixes
  const isDesktop = md

  // 2. Resolve the current alignment mode based on screen size
  const currentAlignment = isDesktop ? desktopAlignment : mobileAlignment

  // 3. Map alignment to Tailwind classes
  const textAlign = currentAlignment === 'center' ? 'text-center' : 'text-left'

  // Note: Grid uses 'start', not 'left'
  const justifyItems =
    currentAlignment === 'center' ? 'justify-items-center' : 'justify-items-start'

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.25, once: true }}
      variants={containerStagger(0.2, 0.33)}
      // We removed the hardcoded 'md:justify-items-start' and replaced it with our variable
      className={`grid gap-3 ${justifyItems}`}
    >
      <motion.h1
        // We removed hardcoded 'text-center' and 'md:text-left'
        className={`font-bold text-4xl text-primary-light brightness-75 lg:text-7xl ${textAlign}`}
        variants={fadeUp(1)}
      >
        {title}
      </motion.h1>

      <motion.p
        // We removed hardcoded 'text-center' and 'md:text-left'
        className={`max-w-5xl text-bg-dark text-md lg:text-2xl ${textAlign}`}
        variants={fadeUp(1)}
      >
        {subTitle}
      </motion.p>

      <motion.div
        className="mt-3 flex gap-3"
        variants={fadeUp(1)}
      >
        {children}
      </motion.div>
    </motion.div>
  )
}
